import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";
import cx from "classnames";
import styles from "./hubColumn.module.scss";
import { HubLayoutColumn, HubLayoutSection } from "../../../types/hub";
import { useDispatch, useSelector } from "react-redux";
import { fetchHubSection } from "../../../redux/actions/hub";
import { RootState } from "../../../redux/store";
import Carousel from "../../../components/carousel";
import _ from "lodash";
import dateformat from "dateformat";
import {
  CollectionCard,
  PlaceholderCollectionCard,
} from "../components/collectionCard";
import { AssetCard, PlaceholderAssetCard } from "../components/assetCard";
import {
  ListAssetCard,
  ListPlaceholderAssetCard,
} from "../components/assetCard/listAssetCard";
import { StreamCard, PlaceholderStreamCard } from "../components/streamCard"; // Import your METRO component
import MetroAssetCard, { PlaceholderMetroContentCard } from "../components/assetCard/metroAssetCard";
import {
  CoachingPromotedContentCard,
  PlaceholderPromotedContentCard,
  PromotedContentCard,
} from "../components/promotedContent";
import { ScrollableContainer } from "src/components/scroller";
import {
  CourseCard,
  PodcastCard,
  SimulationCard,
} from "src/pages/coaching/explore/card";
import useMeasure from "react-use-measure";
import { useHubAdoption } from "src/redux/actions/adoption/hub";
import { useNavigate } from "react-router-dom";
import userRoleService from "src/utils/userRoles.service";
import { CoachingCard } from "../components/coachingCard";

type HubColumnProps = {
  rowIndex: number;
  section: HubLayoutSection;
  width: number;
};


const LeftArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.left)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const RightArrow: React.FC<any> = () => {
  return (
    <div className={cx(styles.arrow, styles.right)}>
      <i className="paperflite-arrow-up"></i>
    </div>
  );
}

const COACHING_ROUTES = {
  published_learnings: "/coaching/explore",
  incomplete_learnings: "/coaching/courses",
} as const;

export default function HubColumn({ section }: HubColumnProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [carouselRef, carouselBounds] = useMeasure();

  const smartFilter = useSelector(
    (state: RootState) => state.hub?.sections[_.get(section,"id","")]?.smartFilter,
  );

  const sectionLoading = useSelector(
    (state: RootState) => state.hub?.sections[_.get(section,"id","")]?.loading,
  );
  const items = useSelector(
    (state: RootState) => state.hub?.sections[_.get(section,"id","")]?.items,
  );

  useEffect(() => {
    dispatch(fetchHubSection(_.get(section,"id","")));
  }, [section.id]);

  const showCoachingViewAll =
    section.entityType === "coaching" &&
    section.layoutStyle !== "promoted_content";
  
  const coachingRoute = COACHING_ROUTES[section.smartFilter ?? ""] ?? null;

  const renderSectionTitle = (
    <p className={styles.sectionTitle}>
      {section.title}
      {showCoachingViewAll && coachingRoute && (
        <span
          className={styles.viewAll}
          onClick={() => navigate(coachingRoute)}
        >
          view all <i className={cx("paperflite-dropdown",styles.dropdown)} />
        </span>
      )}
    </p>
  );

  const { trackCollectionViewedEvent } = useHubAdoption();

  if(_.size(items) === 0 && !sectionLoading){
    return null;
  }

  if (
    section.entityType === "collection" &&
    !userRoleService.canAccessCollection()
  ) {
    return null;
  }


  return (
    <div className={styles.hubSection} ref={carouselRef} >
      {_.size(items) > 0 && renderSectionTitle}
      {_.get(section, "layoutStyle") === "list" ? (
        <div>
          <div className={styles.renderList}>
            {renderListItems(items, sectionLoading)}
          </div>
        </div>
      ) : _.get(section, "layoutStyle") === "metro" ? (
        <div>
          {sectionLoading && <PlaceholderMetroContentCard/>}
          {!sectionLoading && <div className={styles.renderMetro}>
            {renderMetroItems(items, section, sectionLoading)}
          </div>}
        </div>
      ) : _.get(section, "layoutStyle") === "promoted_content" ? (
        <div className={styles.promotedContent}>
          {!sectionLoading && items?.length > 0 && (
            section.entityType === "coaching" ? (
              renderPromotedCoachingLearningCard(items[0], navigate)
            ) : (
              <a
                onClick={() => {
                  if(items[0]?.entityType === "collection"){
                    trackCollectionViewedEvent(_.get(items,"[0].name",""));
                  }
                  navigate(items[0]?.entityType === "asset"
                    ? `/assets/${items[0]?.id}`
                    : `/collections/${items[0]?.id}`);
                }}
              >
                <PromotedContentCard
                  id={items[0]?.id}
                  icon={
                    items[0]?.entityType === "asset"
                      ? items[0]?.icon
                      : items[0]?.banner?.background
                  }
                  name={items[0]?.name}
                  item={items[0]}
                  description={
                    items[0]?.entityType === "asset"
                      ? items[0]?.summary
                      : items[0]?.template
                  }
                />
              </a>
            )
          )}
          {sectionLoading && <PlaceholderPromotedContentCard />}
        </div>
      ) : (
        <ScrollableContainer distance={carouselBounds.width * 0.8 || 1000} wrapperClassName={styles.wrapperContainer} leftArrowComponent={LeftArrow} rightArrowComponent={RightArrow}>
          {renderItems(items, section, sectionLoading, smartFilter)}
        </ScrollableContainer>
      )}
    </div>
  );
}

function renderMetroItems(items: any[], section: any, sectionLoading: boolean) { 
  // return items?.map((item, index) => (
  return <MetroAssetCard items={items} />;
  // ));
}

function renderListItems(items: any[], sectionLoading: boolean) {
  return sectionLoading
    ? _.times(3).map((i) => <ListPlaceholderAssetCard key={i} />)
    : items
        ?.slice(0, 3)
        .map((item: any) => <ListAssetCard key={item.id} {...item} />);
}

function renderPromotedCoachingLearningCard(item: any, navigate: any) {
  const course = _.get(item, "course", null);
  const entityType = item.courseType?.toLowerCase() ?? "";

  return (
    <a onClick={() => navigate(`/coaching/explore/${entityType}s/${course.id}`)}>
      <CoachingPromotedContentCard
        id={course.id}
        icon={_.get(course, "banner.background")}
        name={_.get(course, "name")}
        item={item}
        description={_.get(course, "description")}
      />
    </a>
  );
}

function renderItems(
  items: any[],
  section: any,
  sectionLoading: boolean,
  smartFilter: string,
) {
  const placeholdersMap = {
    collection: _.times(4).map((i) => <PlaceholderCollectionCard key={i} />),
    stream: _.times(4).map((i) => (
      <PlaceholderStreamCard key={i} cardStyle={section.cardStyle} />
    )),
    asset: _.times(8).map((i) => <PlaceholderAssetCard key={i} />),
    coaching: _.times(4).map((i) => <PlaceholderAssetCard key={i} />),
  };

  const sortedStreams = items.sort((a: any, b: any) => {
    if (a.lastViewed === null && b.lastViewed !== null) return 1;
    if (a.lastViewed !== null && b.lastViewed === null) return -1;
    if (a.lastViewed > b.lastViewed) return -1;
    if (a.lastViewed < b.lastViewed) return 1;
    return 0;
  });

  const cardsMap = {
    collection: items?.map((item: any) => (
      <CollectionCard key={item.id} {...item} />
    )),
    stream: sortedStreams?.map((item: any) => (
      <StreamCard key={item.id} {...item} cardStyle={section.cardStyle} />
    )),
    asset: items?.map((item: any) => (
      <AssetCard
        key={item.id}
        item={item}
        progress={
          smartFilter === "recently_viewed_assets"
            ? item.readingMetrics?.segment
            : null
        }
      />
    )),
    coaching: items?.map((item: any) => (
      <CoachingCard key={item.id} learning={item} filter={smartFilter} />
    ))
  };

  if (section.entityType in placeholdersMap) {
    const entityType = section.entityType as keyof typeof placeholdersMap;

    return sectionLoading ? placeholdersMap[entityType] : cardsMap[entityType];
  }

  return null; // Return null or handle the default case if needed
}
